// pages/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Rating,
  Skeleton,
  Paper,
  IconButton,
  Avatar,
  AvatarGroup,
  Fade,
  Grow
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Restaurant as RestaurantIcon,
  DeliveryDining as DeliveryIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  Storefront as StorefrontIcon,
  TrendingUp as TrendingIcon,
  LocalOffer as OfferIcon,
  Fastfood as FastfoodIcon,
  EmojiEvents as AwardIcon
} from '@mui/icons-material';
import WelcomePopup from '../components/WelcomePopup';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import GoogleMapModal from '../components/GoogleMapModal';
import CuisineFilter from '../components/CuisineFilter';
import currencyTrans from '../components/CarrancyTrans';

const HomePage = ({keywordsData,language}) => {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  
  // Location states
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [isSelectMap, setIsSelectMap] = useState(false);
  const [openWelcomeModel, setOpenWelcomeModel] = useState(false);

  // Restaurant states
  const [featuredRestaurants, setFeaturedRestaurants] = useState([]);
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [popularRestaurants, setPopularRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cuisineTypes, setCuisineTypes] = useState([]);
  const [selectedCuisine, setSelectedCuisine] = useState('all');

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Google Maps API
  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
  
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      const api = 'AIzaSyBl9P55wDymA5tQzFgWFaZWgMyQMdOSWXA';
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps API Loaded");
        callback();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API.");
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          callback();
        }
      }, 1000);
    }
  };

  // Fetch places for autocomplete
  const fetchPlaces = async (input) => {
    if (isSelectMap) return;
    
    try {
      const response = await api.get(`/autocomplete?input=${input}&language=${language}`);
      const data = response.data;
      if (data.predictions) {
        setSuggestions(data.predictions.map((place) => place.description));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  };

  // Handle location selection from search
  const handleLocationSelect = (location) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(location));
      return;
    }

    if (location.address) {
      // Direct location from map
      setQuery(location.address);
      localStorage.setItem('user_location', JSON.stringify(location));
      setLocation({
        lat: location.lat,
        lng: location.lng,
        address: location.address
      });
      fetchNearbyRestaurants(location.lat, location.lng);
      setIsSelectMap(true);
      setShowSuggestions(false);
    } else {
      // From search suggestions
      setQuery(location);
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: location }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          setSelectedLocation({
            lat: loc.lat(),
            lng: loc.lng(),
            address: location,
          });
          setOpenMap(true);
        }
      });
      setShowSuggestions(false);
    }
    setSuggestions([]);
  };

  // Handle auto-detect location
  const handleAutoDetect = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setSelectedLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setOpenMap(true);
      },
      (error) => {
        alert(language === 'ar' ? keywordsData.enable_gps_ar : keywordsData.enable_gps_en);
      }
    );
  };

  // Handle search input change
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    clearTimeout(window.debounceTimeout);
    window.debounceTimeout = setTimeout(() => {
      if (value.length > 2) {
        fetchPlaces(value);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 500);
  };
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
// In HomePage.jsx - Update fetchNearbyRestaurants
const fetchNearbyRestaurants = async (lat, lng) => {
  try {
    setLoading(true);
    
    const params = { 
      lat, 
      lng, 
      radius: 10,
      sortBy: 'distance', // or 'rating', 'newest'
      page: 1,
      limit: 20
    };
    
    if (selectedCuisines.length > 0) {
      params.cuisineIds = selectedCuisines.join(',');
    }
    
    const response = await api.get('/restaurants/nearby', { params });
    
    if (response.data.success) {
      setNearbyRestaurants(response.data.restaurants || []);
      // You can also save pagination info if needed
    }
  } catch (error) {
    console.error('Error fetching nearby restaurants:', error);
  } finally {
    setLoading(false);
  }
};

// Add function to fetch newest restaurants
const fetchNewestRestaurants = async () => {
  try {
    const response = await api.get('/restaurants/newest?limit=10');
    if (response.data.success) {
      // You can set these to a different state if you want
      setNearbyRestaurants(response.data.restaurants || []);
    }
  } catch (error) {
    console.error('Error fetching newest restaurants:', error);
  }
};

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Check for saved location
        const savedLocation = localStorage.getItem('user_location');
        if (savedLocation) {
          const loc = JSON.parse(savedLocation);
          setLocation(loc);
          setQuery(loc.address);
          fetchNearbyRestaurants(loc.lat, loc.lng);
        } else {
          setLoading(false);
        }
      //const [featuredRes, popularRes, cuisinesRes, categoriesRes] = await Promise.all([
        //api.get('/restaurants/featured'),
        //api.get('/restaurants/popular'),
        //api.get('/restaurants/cuisines'),
        //api.get('/restaurants/categories/all')
      //]);
      //const [cuisinesRes] = await Promise.all([
       // api.get('/restaurants/cuisines'),
      //]);

      //setFeaturedRestaurants(featuredRes.data || []);
      //setPopularRestaurants(popularRes.data || []);
      //setCuisineTypes(cuisinesRes.data || []);
      //setCategories(categoriesRes.data || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Refetch when cuisine filter changes
  useEffect(() => {
    if (location) {
      fetchNearbyRestaurants(location.lat, location.lng);
    }
  }, [selectedCuisine]);

  const handleRestaurantClick = (restaurantId) => {
    navigate(`/restaurant/${restaurantId}`);
  };

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh' ,py: 6, 
      mb: 10,}}>
      {/* Hero Section with Search */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
        borderBottom: '2px solid #FFD700',
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background elements */}
        <Box sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(255, 215, 0, 0.03)',
          filter: 'blur(80px)',
          animation: 'float 20s infinite ease-in-out'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(255, 215, 0, 0.03)',
          filter: 'blur(80px)',
          animation: 'float 15s infinite ease-in-out reverse'
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Grow in timeout={1000}>
              <Typography 
                variant="h1" 
                sx={{ 
                  color: '#FFD700',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontWeight: 900,
                  mb: 2,
                  textShadow: '0 2px 20px rgba(255, 215, 0, 0.3)',
                  letterSpacing: '2px',
                  animation: 'glow 3s infinite'
                }}
              >
                {language === 'ar' ? 'بائع أم شاري؟' : 'Payer or Seller?'}
              </Typography>
            </Grow>
            
            <Fade in timeout={1500}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#FFFFFF',
                  fontSize: { xs: '1.2rem', md: '1.8rem' },
                  fontWeight: 500,
                  mb: 4,
                  opacity: 0.9
                }}
              >
                {language === 'ar' 
                  ? 'دعنا نجد لك أفضل المتاجر القريبة' 
                  : "Let's find you the best store nearby"}
              </Typography>
            </Fade>

            {/* Search Bar with Suggestions */}
            <Box ref={searchRef} sx={{ position: 'relative', maxWidth: '600px', mx: 'auto' }}>
              <div className="search-bar">
                <input
                  id='area_serach'
                  name='area_serach'
                  className="input"
                  type="text"
                  placeholder={language === "ar" ? keywordsData.search_match_location_place_holder_ar : keywordsData.search_match_location_place_holder_en}
                  value={query}
                  onChange={handleQueryChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  required
                />
                
                <button 
                  className="location-button"
                  onClick={handleAutoDetect}
                >
                  <MyLocationIcon />
                </button>
              </div>

              {/* Location Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <Fade in>
                  <ul className="location-list">
                    {suggestions.map((place, index) => (
                      <li
                        key={index}
                        className="location-item"
                        onClick={() => handleLocationSelect(place)}
                      >
                        <LocationIcon sx={{ fontSize: 18, mr: 1, color: '#FFD700' }} />
                        {place}
                      </li>
                    ))}
                  </ul>
                </Fade>
              )}
            </Box>

            {/* Popular Searches */}
            <Box sx={{ mt: 4, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
  <Button
    variant="contained"
    onClick={() => setOpenWelcomeModel(true)}
    sx={{
      backgroundColor: '#FFD700',
      color: '#000000',
      '&:hover': {
        backgroundColor: '#FFE55C',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)'
      },
      transition: 'all 0.2s ease',
      px: 3,
      py: 1,
      borderRadius: '50px',
      fontWeight: 700,
      fontSize: '1rem'
    }}
  >
    {language === 'ar' ? 'كن شريكاً' : 'Become a Partner'}
  </Button>
</Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Cuisine Filters with Scroll */}
        <Box sx={{ 
          mb: 4, 
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': {
            height: 4,
          },
          '&::-webkit-scrollbar-track': {
            background: '#1A1A1A',
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#FFD700',
            borderRadius: 2,
          }
        }}>
          <Box sx={{ display: 'flex', gap: 1, minWidth: 'max-content' }}>
            <Chip
              label={language === 'ar' ? 'الكل' : 'All'}
              onClick={() => setSelectedCuisine('all')}
              sx={{
                backgroundColor: selectedCuisine === 'all' ? '#FFD700' : 'transparent',
                color: selectedCuisine === 'all' ? '#000000' : '#FFFFFF',
                border: '1.5px solid #FFD700',
                fontWeight: 600,
                px: 3,
                py: 2.5,
                '&:hover': {
                  backgroundColor: '#FFD700',
                  color: '#000000'
                }
              }}
            />
            {cuisineTypes.map((cuisine) => (
              <Chip
                key={cuisine.id}
                label={language === 'ar' ? cuisine.name : cuisine.name_en}
                onClick={() => setSelectedCuisine(cuisine.id)}
                sx={{
                  backgroundColor: selectedCuisine === cuisine.id ? '#FFD700' : 'transparent',
                  color: selectedCuisine === cuisine.id ? '#000000' : '#FFFFFF',
                  border: '1.5px solid #FFD700',
                  fontWeight: 600,
                  px: 3,
                  py: 2.5,
                  '&:hover': {
                    backgroundColor: '#FFD700',
                    color: '#000000'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Featured Restaurants Section */}
        {featuredRestaurants.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <AwardIcon sx={{ color: '#FFD700', fontSize: 32 }} />
              <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
                {language === 'ar' ? 'متاجر مميزة' : 'Featured Store'}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {featuredRestaurants.map((restaurant) => (
                <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                  <RestaurantCard 
                    restaurant={restaurant} 
                    onClick={() => handleRestaurantClick(restaurant.id)}
                    language={language}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Nearby Restaurants Section */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <LocationIcon sx={{ color: '#FFD700', fontSize: 32 }} />
            <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
              {location 
                ? (language === 'ar' ? 'متاجر قريبة منك' : 'Store near you')
                : (language === 'ar' ? 'متاجر مقترحة' : 'Suggested Store')}
              <p></p>
              {location && (
              <Chip
                label={location.address}
                size="small"
                sx={{ ml: 'auto', bgcolor: '#1A1A1A', color: '#B3B3B3',maxWidth:300 }}
              />
            )}
            </Typography>
          </Box>
          
          {loading ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={280} sx={{ bgcolor: '#1A1A1A', borderRadius: 3 }} />
                </Grid>
              ))}
              {cuisineTypes.length > 0 && (
  <CuisineFilter
    cuisines={cuisineTypes}
    selectedCuisines={selectedCuisines}
    onCuisineChange={setSelectedCuisines}
  />
)}
            </Grid>
          ) : nearbyRestaurants.length > 0 ? (
            <Grid container spacing={3}>
              {nearbyRestaurants.map((restaurant) => (
                <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                  <RestaurantCard 
                    restaurant={restaurant} 
                    onClick={() => handleRestaurantClick(restaurant.id)}
                    language={language}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ 
              p: 6, 
              textAlign: 'center', 
              bgcolor: '#0D0D0D',
              border: '2px dashed #2D2D2D',
              borderRadius: 4
            }}>
              <RestaurantIcon sx={{ fontSize: 80, color: '#2D2D2D', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#B3B3B3', mb: 2 }}>
                {language === 'ar' 
                  ? 'لا توجد متاجر في هذه المنطقة بعد' 
                  : 'No store in this area yet'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setSelectedCuisine('all')}
                sx={{ borderColor: '#FFD700', color: '#FFD700' }}
              >
                {language === 'ar' ? 'عرض الكل' : 'View All'}
              </Button>
            </Paper>
          )}
        </Box>

        {/* Popular Restaurants Section */}
        {popularRestaurants.length > 0 && (
          <Box sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <TrendingIcon sx={{ color: '#FFD700', fontSize: 32 }} />
              <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700 }}>
                {language === 'ar' ? 'الأكثر طلباً' : 'Most Popular'}
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {popularRestaurants.map((restaurant) => (
                <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
                  <RestaurantCard 
                    restaurant={restaurant} 
                    onClick={() => handleRestaurantClick(restaurant.id)}
                    language={language}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Categories Grid */}
        {categories.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700, mb: 3 }}>
              {language === 'ar' ? 'تسوق حسب الفئة' : 'Store by Category'}
            </Typography>
            <Grid container spacing={2}>
              {categories.map((category) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={category.id}>
                  <Paper
                    onClick={() => navigate(`/category/${category.id}`)}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      bgcolor: '#0D0D0D',
                      border: '1px solid #2D2D2D',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#FFD700',
                        transform: 'scale(1.05)',
                        bgcolor: '#1A1A1A',
                        '& .category-icon': {
                          color: '#FFD700'
                        }
                      }
                    }}
                  >
                    <Typography variant="h3" className="category-icon" sx={{ color: '#FFFFFF', mb: 1 }}>
                      {category.icon || '🍽️'}
                    </Typography>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                      {language === 'ar' ? category.name : category.name_en}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
      <WelcomePopup isOpen={openWelcomeModel} isPart={true} onClose={() => setOpenWelcomeModel(false)}/>
      {/* Google Maps Modal */}
      <GoogleMapModal
        open={openMap}
        handleClose={() => setOpenMap(false)}
        onLocationSelect={(location) => handleLocationSelect(location)}
        initialCenter={selectedLocation}
        language={language}
        keywordsData={keywordsData}
      />

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(20px, -20px) rotate(5deg); }
          66% { transform: translate(-20px, 20px) rotate(-5deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        @keyframes glow {
          0% { text-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
          50% { text-shadow: 0 0 40px rgba(255, 215, 0, 0.6); }
          100% { text-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
        }
      `}</style>
    </Box>
  );
};

const RestaurantCard = ({ restaurant, onClick ,language}) => {
  const [imageError, setImageError] = useState(false);
  
  // Get open status from menu_settings
  const isOpen = restaurant.menu_settings?.is_open ?? true;
  const openStatusText = isOpen 
    ? (language === 'ar' ? 'مفتوح' : 'Open') 
    : (language === 'ar' ? 'مغلق' : 'Closed');
  const openStatusColor = isOpen ? '#4CAF50' : '#FF3D00';

  return (
    <Card 
      onClick={onClick}
      sx={{
        bgcolor: '#0D0D0D',
        border: '1px solid #2D2D2D',
        borderRadius: '24px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          borderColor: '#FFD700',
          boxShadow: '0 12px 40px rgba(255, 215, 0, 0.25)',
          '& .restaurant-overlay': {
            opacity: 1
          },
          '& .restaurant-image': {
            transform: 'scale(1.1)'
          }
        }
      }}
    >
      {/* Open Status Badge - Top Left */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          backgroundColor: `${openStatusColor}dd`,
          backdropFilter: 'blur(4px)',
          padding: '4px 10px',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            animation: isOpen ? 'pulse 1.5s infinite' : 'none'
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.5px'
          }}
        >
          {openStatusText}
        </Typography>
      </Box>

      {/* Image Container */}
      <Box sx={{ position: 'relative', overflow: 'hidden', height: 180 }}>
        <CardMedia
          component="img"
          className="restaurant-image"
          height="180"
          image={!imageError ? (restaurant.cover_image || restaurant.logo || '/default-restaurant.jpg') : '/default-restaurant.jpg'}
          alt={restaurant.name}
          onError={() => setImageError(true)}
          sx={{ 
            transition: 'transform 0.5s ease',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px'
          }}
        />
        
        {/* Overlay with quick actions */}
        <Box
          className="restaurant-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#FFD700',
              color: '#000000',
              '&:hover': { bgcolor: '#FFE55C' }
            }}
          >
            {language === 'ar' ? 'عرض القائمة' : 'View Menu'}
          </Button>
        </Box>

        {/* Rating Badge */}
        <Chip
          label={`${restaurant.rating || '4.5'} ⭐`}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            bgcolor: '#FFD700',
            color: '#000000',
            fontWeight: 700,
            border: '2px solid #000000',
            zIndex: 2
          }}
        />

        {/* Distance Badge */}
        {restaurant.distance && (
          <Chip
            label={`${restaurant.distance.toFixed(1)} km`}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              bgcolor: 'rgba(0,0,0,0.7)',
              color: '#FFD700',
              fontWeight: 600,
              border: '1px solid #FFD700',
              backdropFilter: 'blur(4px)',
              zIndex: 2
            }}
          />
        )}

        {/* Offer Badge */}
        {restaurant.offer && (
          <Chip
            icon={<OfferIcon sx={{ fontSize: 14 }} />}
            label={restaurant.offer}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: '#FF3D00',
              color: '#FFFFFF',
              fontWeight: 600,
              border: '2px solid #000000',
              zIndex: 2
            }}
          />
        )}
      </Box>

      <CardContent sx={{ p: 2 }}>
        {/* Restaurant Name */}
        <Typography variant="h6" sx={{ 
          color: '#FFFFFF', 
          fontWeight: 700,
          mb: 1,
          fontSize: '1.1rem',
          lineHeight: 1.3
        }}>
          {language === 'ar' ? restaurant.name : restaurant.name_en}
        </Typography>
        
        {/* Primary Cuisine Type */}
        {restaurant.cuisine_type && (
          <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 1 }}>
            {language === 'ar' ? restaurant.cuisine_type : restaurant.cuisine_type_en || restaurant.cuisine_type}
          </Typography>
        )}
        
        {/* Multiple Cuisines Chips */}
        {restaurant.cuisines && restaurant.cuisines.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
            {restaurant.cuisines.slice(0, 3).map((cuisine) => (
              <Chip
                key={cuisine.id}
                icon={<span>{cuisine.icon || '🍽️'}</span>}
                label={language === 'ar' ? cuisine.name : cuisine.name_en}
                size="small"
                sx={{
                  backgroundColor: '#1A1A1A',
                  color: '#FFD700',
                  border: '1px solid #2D2D2D',
                  height: 24,
                  '& .MuiChip-icon': {
                    color: '#FFD700',
                    fontSize: 14,
                    ml: 0.5
                  }
                }}
              />
            ))}
            {restaurant.cuisines.length > 3 && (
              <Chip
                label={`+${restaurant.cuisines.length - 3}`}
                size="small"
                sx={{
                  backgroundColor: '#1A1A1A',
                  color: '#B3B3B3',
                  border: '1px solid #2D2D2D',
                  height: 24
                }}
              />
            )}
          </Box>
        )}
        
        {/* Delivery Info */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TimeIcon sx={{ fontSize: 16, color: '#FFD700' }} />
            <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
              {restaurant.delivery_time_min || '25-35'} min
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DeliveryIcon sx={{ fontSize: 16, color: '#FFD700' }} />
            <Typography variant="caption" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
              {restaurant.delivery_fee || '5'} {currencyTrans(restaurant.currency,language)}
            </Typography>
          </Box>
        </Box>

        {/* Minimum Order */}
        <Typography variant="caption" sx={{ color: '#6B6B6B', display: 'block' }}>
          {language === 'ar' ? 'الحد الأدنى للطلب: ' : 'Min. order: '}
          {restaurant.min_order_amount || '20'} {currencyTrans(restaurant.currency,language)}
        </Typography>

        {/* Closed Message (Optional) - Show a small indicator when closed */}
        {!isOpen && (
          <Box sx={{ 
            mt: 1, 
            pt: 1, 
            borderTop: '1px solid #2D2D2D',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            <TimeIcon sx={{ fontSize: 12, color: '#FF3D00' }} />
            <Typography variant="caption" sx={{ color: '#FF3D00', fontSize: '0.65rem' }}>
              {language === 'ar' ? 'مغلق حالياً' : 'Currently Closed'}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </Card>
  );
};

export default HomePage;